import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import Box from '@tds/core-box';
import Select from '@tds/core-select';
import Input from '@tds/core-input';
import Button from '@tds/core-button';
import DimpleDivider from '@tds/core-dimple-divider';
import { Delete } from '@tds/core-interactive-icon';
import { Accordion } from '@tds/core-expand-collapse';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useLocaleContext } from '../../../context/LocaleContext';
import { useTranslationsContext } from '../../../context/TranslationsContext';
import { config, apiHost } from '../../../utils/request';
import { Heading } from '../css';
import {
  accessoryCategoryKeys,
  accessoryValues
} from '../../../utils/constant';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';

const Accessories = ({ selectedAccessories, setSelectedAccessories }) => {
  const locale = useLocaleContext();
  const {
    WLS: {
      WIRELESS_ORDER_DETAILS: {
        Accessories: {
          category,
          product,
          sku,
          price,
          salesTax,
          categoryValues
        }
      }
    }
  } = locale === 'en-US' ? EN : FR;
  const {
    WLS: {
      WIRELESS_ORDER_DETAILS: {
        Accessories: { Accessories, AddAccessory, AccessoryErrorMessage }
      }
    }
  } = useTranslationsContext();

  const frenchAccessoryCategories = FR.WLS.WIRELESS_ORDER_DETAILS.Accessories.categoryValues;
  const englishAccessoryCategories = EN.WLS.WIRELESS_ORDER_DETAILS.Accessories.categoryValues;

  const accessoryCategoriesFrenchToEnglishMap = {
    [frenchAccessoryCategories.cases]: englishAccessoryCategories.cases,
    [frenchAccessoryCategories.screenProtection]: englishAccessoryCategories.screenProtection,
    [frenchAccessoryCategories.charging]: englishAccessoryCategories.charging,
    [frenchAccessoryCategories.audio]: englishAccessoryCategories.audio,
  }

  const [accessoryCategory, setAccessoryCategory] = useState({
    value: '',
    error: ''
  });
  const [accessoryProduct, setAccessoryProduct] = useState({
    value: '',
    error: ''
  });
  const deviceSearchRef = useRef(null);
  const [productOptions, setProductOptions] = useState([]);
  const [accessoryFields, setAccessoryFields] = useState([]);
  const [areAccessoriesLoading, setAccessoriesLoading] = useState(false);
  const [categorySearchParam, setCategorySearchParam] = useState('');

  const products = async () => {
    productOptions.length = 0;
    const province = localStorage.getItem('provinceWLS');
    setAccessoriesLoading(true);
    const res = await axios.get(`${apiHost}/wls/accessories`, {
      ...config(),
      params: {
        category: categorySearchParam,
        province
      }
    });
    setAccessoriesLoading(false);
    if (res && res.data && res.data.data && res.data.data.rows) {
      for (let i = 0; i < res.data.data.rows.length; i++) {
        setProductOptions(options => [
          ...options,
          {
            text: locale === 'en-US' ? res.data.data.rows[i].product : res.data.data.rows[i].product_fr,
            value: locale === 'en-US' ? res.data.data.rows[i].product : res.data.data.rows[i].product_fr,
            valueFr: res.data.data.rows[i].product_fr,
            sku: res.data.data.rows[i].sku,
          }
        ]);
        setAccessoryFields(fields => [
          ...fields,
          {
            category: res.data.data.rows[i].category,
            product: locale === 'en-US' ? res.data.data.rows[i].product : res.data.data.rows[i].product_fr,
            msrp: res.data.data.rows[i].msrp,
            sku: res.data.data.rows[i].sku,
            tax: res.data.data.rows[i].tax
          }
        ]);
      }
    }
    setAccessoryProduct({
      value: ''
    });
    return [];
  };

  useEffect(() => {
    if (accessoryCategory.value) products();
  }, [accessoryCategory]);

  const getProductOptions = () => {
    return (
      <Box width={30} mdWidth={100} pb={24} pr={16}>
        <div className="main-content-form-headers">
          <span>{product}</span>
        </div>
        <Typeahead
          ref={deviceSearchRef}
          style={{ marginTop: 5 }}
          id={uuidv4()}
          labelKey={'text'}
          placeholder={'Enter name or SKU'}
          filterBy={['value', 'sku', 'valueFr']}
          name={'AccessorySearch'}
          isLoading={areAccessoriesLoading}
          onChange={value => {
            if (value.length) {
              setAccessoryProduct({
                status: '',
                error: '',
                value: value[0].value,
              });
            } else {
              setAccessoryProduct({
                status: '',
                error: '',
                value: '',
              });
            }
          }}
          positionFixed
          options={productOptions}
        />
      </Box>
    );
  };
  var fields = [];
  const getAccessoryFields = () => {
    fields = accessoryFields.find(p => p.product === accessoryProduct.value);
    return (
      <>
        <Input readOnly id="SKU" label={sku} value={fields.sku} />
        <Input
          readOnly
          id="Price (without taxes)"
          label={price}
          value={`$${fields.msrp}`}
        />
        {/* <Input
          readOnly
          id="Sales Tax"
          label={salesTax}
          value={`$${fields.tax}`}
        /> */}
      </>
    );
  };

  const addAccessory = () => {
    if (!accessoryCategory.value)
      setAccessoryCategory({
        ...accessoryCategory,
        status: 'error',
        error: AccessoryErrorMessage
      });
    if (!accessoryProduct.value)
      setAccessoryProduct({
        ...accessoryProduct,
        status: 'error',
        error: AccessoryErrorMessage
      });
    if (accessoryCategory.value && accessoryProduct.value) {
      setSelectedAccessories([
        ...selectedAccessories,
        {
          id: uuidv4(),
          category: accessoryCategory.value,
          product: accessoryProduct.value,
          sku: fields.sku,
          price: parseFloat(fields.msrp),
          tax: parseFloat(fields.tax)
        }
      ]);
      setAccessoryCategory({ value: '', error: '' });
      setAccessoryProduct({ value: '', error: '' });
    }
  };

  const removeAccessory = id => {
    const updatedList = selectedAccessories.filter(
      accessory => accessory.id !== id
    );
    setSelectedAccessories(updatedList);
  };
  const categories = accessoryCategoryKeys.map(key => {
    return {
      text: categoryValues[key],
      value: categoryValues[key]
    }
  });
  return (
    <>
      <div style={{ margin: '1rem 0' }}>
        <Heading level="h4">{Accessories}</Heading>
      </div>
      <Accordion tag="h3">
        <Accordion.Panel id="accessories" header={Accessories}>
          <Box between={2}>
            <Select
              id={uuidv4()}
              label={category}
              placeholder="Please select..."
              options={categories}
              type="select"
              error={accessoryCategory.error}
              value={accessoryCategory.value}
              onChange={e => {
                setAccessoryCategory({
                  status: '',
                  error: '',
                  value: e.target.value
                });
                setCategorySearchParam(locale === 'fr-CA'
                  ? accessoryCategoriesFrenchToEnglishMap[e.target.value]
                  : e.target.value
                )
                deviceSearchRef.current.clear();
              }}
            />
            {accessoryCategory.value && getProductOptions()}
            {accessoryProduct.value && getAccessoryFields()}
            <Button id="features" onClick={addAccessory}>{AddAccessory}</Button>
            {selectedAccessories.length > 0 && (
              <>
                <DimpleDivider />
                <div class="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead style={{ backgroundColor: '#f2f2f2' }}>
                      <tr>
                        <th scope="col">{accessoryValues.id}</th>
                        <th scope="col">{accessoryValues.Product}</th>
                        <th scope="col">{accessoryValues.Price}</th>
                        {/* <th scope="col">{accessoryValues.Tax}</th> */}
                        <th scope="col">{accessoryValues.Remove}</th>
                      </tr>
                    </thead>
                    {selectedAccessories.map((accessory, index) => (
                      <tbody>
                        <tr>
                          <th scope="row">{index + 1}</th>
                          <td>
                            {accessory.product} - ${accessory.sku}
                          </td>
                          <td>{accessory.price}</td>
                          {/* <td>{accessory.tax}</td> */}
                          <td>
                            <Delete
                              onClick={() => removeAccessory(accessory.id)}
                              a11yText="Delete"
                            />
                          </td>
                        </tr>
                      </tbody>
                    ))}
                  </table>
                </div>
              </>
            )}
          </Box>
        </Accordion.Panel>
      </Accordion>
    </>
  );
};

Accessories.propTypes = {
  selectedAccessories: PropTypes.arrayOf(PropTypes.any).isRequired,
  setSelectedAccessories: PropTypes.func.isRequired
};

export default Accessories;
